/**
 * Shared advisor-tools API client (Sprint C6 A-1; cookie-session cutover SEC-V04).
 *
 * The advisor session lives in an HttpOnly cookie the backend sets on
 * login/totp/exchange/refresh; the SPA holds NO bearer token in browser storage.
 * Both typed clients are created with `credentials: 'include'` (defaulted in the
 * @aaimplatform/institutional-types factories), so the cookie rides along on every
 * request and the @aaimplatform/database cookie->Bearer shim authenticates it
 * server-side. No Authorization header is injected from storage here.
 *
 * Sprint C10 sweep: detect auth-broken responses (401, 400 TENANT_SCOPE_REQUIRED,
 * 400 AUTH_REQUIRED) and bounce to /login. Mirrors the platform-admin guard
 * landed in bf402818f. The bounce triggers a fresh page load whose AuthContext
 * init re-runs GET /api/v1/auth/me, resolving the user (or clearing it).
 */
import { createAdvisorToolsClient, createConnectorAttestClient } from '@aaimplatform/institutional-types/api-clients';
import type { Middleware } from 'openapi-fetch';

// Legacy localStorage key from the pre-cookie session. Cleared on an auth-broken
// bounce so no stale bearer token lingers in browser storage.
const LEGACY_AUTH_STORAGE_KEY = 'aaim-advisor-tools-auth';

/**
 * Detect responses that indicate the user's auth state is broken:
 * - 401 (no/invalid session)
 * - 400 with `code: 'TENANT_SCOPE_REQUIRED'` (session decoded but missing tenantId claim)
 * - 400 with `code: 'AUTH_REQUIRED'` (server rejected before tenant context lookup)
 */
async function isAuthBrokenResponse(response: Response): Promise<boolean> {
  if (response.status === 401) return true;
  if (response.status === 400) {
    try {
      const body = (await response.clone().json()) as
        | { error?: { code?: string }; code?: string }
        | undefined;
      const code = body?.error?.code ?? body?.code;
      return code === 'TENANT_SCOPE_REQUIRED' || code === 'AUTH_REQUIRED';
    } catch {
      return false;
    }
  }
  return false;
}

/**
 * Clear the legacy persisted auth key and redirect to /login. Idempotent -- skips the
 * redirect if already on /login to avoid loops on the login page itself returning 401.
 */
function bounceToLogin(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(LEGACY_AUTH_STORAGE_KEY);
  } catch {
    // ignore
  }
  if (!window.location.pathname.startsWith('/login')) {
    window.location.href = '/login';
  }
}

// ---------------------------------------------------------------------------
// CSRF double-submit (N8 / GH #1806) -- echo the csrf-token cookie as X-CSRF-Token
// ---------------------------------------------------------------------------

/** Methods that mutate state and therefore carry the CSRF header. */
export const CSRF_MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

// Leading and trailing characters stripped from the decoded csrf-token value: the HTTP
// whitespace a fetch Headers value loses at its ends (tab, LF, CR, space), and the
// whitespace and zero-width characters above U+00FF that a Headers value cannot carry
// (U+1680, U+2000 to U+200D, U+2028, U+2029, U+202F, U+205F, U+2060, U+3000, U+FEFF).
// consumer-api's and advisor-tools-api's connector guards strip the same set, so the
// echoed header equals what they read. Latin-1 whitespace a Headers value keeps
// (U+000B, U+000C, U+00A0) is left in place, because the shared readers behind the other
// advisor backends (packages/database extractTokenFromCookieHeader, aaim-core
// readCookieValue) return it untrimmed. Scanned by index, which stays linear.
function isCsrfCookiePadding(code: number): boolean {
  return (
    code === 0x09 ||
    code === 0x0a ||
    code === 0x0d ||
    code === 0x20 ||
    code === 0x1680 ||
    (code >= 0x2000 && code <= 0x200d) ||
    code === 0x2028 ||
    code === 0x2029 ||
    code === 0x202f ||
    code === 0x205f ||
    code === 0x2060 ||
    code === 0x3000 ||
    code === 0xfeff
  );
}

function stripCsrfCookiePadding(value: string): string {
  let start = 0;
  let end = value.length;
  while (start < end && isCsrfCookiePadding(value.charCodeAt(start))) start++;
  while (end > start && isCsrfCookiePadding(value.charCodeAt(end - 1))) end--;
  return value.slice(start, end);
}

/**
 * Read the NON-HttpOnly `csrf-token` cookie the session issuer set alongside the
 * HttpOnly session cookie. The SPA echoes it as the `X-CSRF-Token` header on
 * mutating requests; the @aaimplatform/database auth middleware enforces the
 * double-submit match (cookie value === header value) for cookie-authenticated
 * requests. Returns null when absent (pre-rollout sessions, or enforcement off).
 *
 * This is the ONE token-shaped value the SPA reads from a cookie, and it is NOT a
 * credential -- it is a per-session CSRF nonce, not a bearer (the session bearer
 * stays in the HttpOnly cookie the SPA cannot read; no no-browser-token-storage
 * concern). Mirrors the admin-portal readCsrfCookie pattern.
 */
export function readCsrfCookie(): string | null {
  if (typeof document === 'undefined') return null;
  for (const pair of document.cookie.split(';')) {
    const idx = pair.indexOf('=');
    if (idx === -1) continue;
    if (pair.slice(0, idx).trim() !== 'csrf-token') continue;
    // Read the cookie the way the connector guards do (GH #2832, #4655): an empty or
    // undecodable value is skipped and the scan continues, so a shadow cookie neither
    // throws out of the middleware nor hides a later valid one. Padding the decode
    // produces (`%20`, `%E2%80%8B`) is stripped from both ends, so the echoed nonce
    // equals the one the guard reads, and a value that is padding only is skipped.
    // Unstripped, a zero-width character makes the header value non-Latin-1, which
    // fetch refuses, and a padded nonce fails the server's double-submit compare.
    const raw = pair.slice(idx + 1).trim();
    if (!raw) continue;
    let decoded: string;
    try {
      decoded = decodeURIComponent(raw);
    } catch {
      continue;
    }
    const unpadded = stripCsrfCookiePadding(decoded);
    if (unpadded.trim().length === 0) continue;
    return unpadded;
  }
  return null;
}

/**
 * Build the CSRF header object for a raw fetch() mutation the openapi-fetch
 * middleware does not cover (e.g. the binary-PDF proposal-generate POST, the
 * logout POST). Returns `{ 'X-CSRF-Token': <value> }` on a mutating method when
 * the csrf-token cookie is present, otherwise `{}` (no-op -> backward-compatible).
 * Spread into the headers of the raw fetch() call.
 */
export function csrfHeader(method: string): Record<string, string> {
  if (!CSRF_MUTATING_METHODS.has(method.toUpperCase())) return {};
  const csrf = readCsrfCookie();
  return csrf ? { 'X-CSRF-Token': csrf } : {};
}

/**
 * Shared openapi-fetch middleware: on mutating methods, echo the non-HttpOnly
 * csrf-token cookie as X-CSRF-Token. Safe methods (GET/HEAD/OPTIONS) carry no
 * header. No-op when the cookie is absent (pre-rollout) so this is
 * backward-compatible. Applied to every mutating openapi-fetch client -- the
 * advisor/connector-attest clients below, plus the page-scoped peValuationClient
 * instances (which `.use(csrfMiddleware)` after creation).
 */
export const csrfMiddleware: Middleware = {
  onRequest({ request }) {
    if (CSRF_MUTATING_METHODS.has(request.method.toUpperCase())) {
      const csrf = readCsrfCookie();
      if (csrf) request.headers.set('X-CSRF-Token', csrf);
    }
    return request;
  },
};

// credentials:'include' is defaulted in createAdvisorToolsClient, so the session
// cookie carries the advisor's auth on every request. No Authorization header is set.
const advisorClient = createAdvisorToolsClient();

// CSRF double-submit echo (N8) on mutating requests.
advisorClient.use(csrfMiddleware);

advisorClient.use({
  async onResponse({ response }) {
    if (await isAuthBrokenResponse(response)) {
      bounceToLogin();
    }
    return response;
  },
});

// Connector-attest client (M-PILOT-03 / ADR-009). Targets the consumer-api
// /api/financial/connector/attest mount. credentials:'include' (defaulted in
// createConnectorAttestClient) carries the HttpOnly session cookie -- the ONLY auth the
// attest sends (browser-token-storage-hardening bts-02b / GH #1806: no body bearer is
// read or forwarded as an Authorization header). The step-up modal re-mints a fresh
// session cookie in place (useAuth().stepUp) before the POST; the attest is same-origin
// (advisor nginx /api/financial -> consumer-api), so the host-only auth-token cookie
// travels and the connector guard reads it + checks 15-minute freshness from the token's
// iat claim. NO bounce-to-login on a 403: the connector step-up modal handles
// re-authentication in place rather than discarding the advisor's keystroked form state.
const connectorAttestClient = createConnectorAttestClient();

// CSRF double-submit echo (N8) on the connector-attest POST. No bounce-to-login
// middleware here (see the client comment above): the connector step-up modal
// handles re-authentication in place rather than discarding the advisor's form state.
connectorAttestClient.use(csrfMiddleware);

export { advisorClient, connectorAttestClient };
